import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Coffee, Loader2, MapPin, Clock, Store, RefreshCw, Footprints } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, eur } from "@/lib/status";
import MiniMap from "@/components/MiniMap";
import api from "@/lib/api";

export default function OrderStatus() {
  const { code } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [route, setRoute] = useState(null);

  const load = () => api.get(`/guest/orders/${code}/status`).then(({ data }) => setOrder(data)).catch(() => setNotFound(true));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [code]);
  useEffect(() => {
    if (order?.mode === "delivery") {
      api.get(`/guest/orders/${code}/route`).then(({ data }) => setRoute(data.available ? data : null)).catch(() => {});
    }
  }, [order?.mode, code]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-6 text-center">
      <Coffee className="w-12 h-12 text-orange" />
      <h1 className="mt-4 text-xl font-bold text-navy">Ordine non trovato</h1>
      <Link to="/" className="mt-4 text-orange font-medium">Torna alla home</Link>
    </div>
  );
  if (!order) return <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="w-8 h-8 text-orange animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-cream">
      <div className="bg-navy text-white px-5 py-8 text-center">
        <span className="text-2xl font-heading font-extrabold">Morning<span className="text-orange">Box</span></span>
        <p className="mt-1 text-white/60 text-sm">Il tuo ordine</p>
      </div>
      <div className="max-w-lg mx-auto px-5 py-6">
        <div className="bg-white rounded-3xl brand-shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-navy/40 uppercase tracking-wider">Codice ordine</p>
              <p className="font-heading font-bold text-2xl text-navy" data-testid="order-code">{order.code}</p>
            </div>
            <StatusBadge status={order.status} testid="order-status-badge" />
          </div>
          <div className="mt-5 space-y-2 text-sm">
            <Row icon={Store} k="Locale" v={order.vendor_name} />
            <Row icon={Coffee} k="Pacchetto" v={`${order.package_name} × ${order.persons} pers.`} />
            <Row icon={MapPin} k="Modalità" v={order.mode === "delivery" ? `Consegna${order.room ? " · camera " + order.room : ""}` : "Ritiro al bar"} />
            <Row icon={Clock} k="Quando" v={`${order.date} · ${order.time_slot}`} />
          </div>
          <div className="mt-5 border-t border-navy/10 pt-4 flex justify-between items-center">
            <span className="text-navy/60 text-sm">Totale ({order.payment_method === "stripe" ? "pagato con carta" : "in struttura"})</span>
            <span className="font-bold text-orange text-lg">{eur(order.total_price)}</span>
          </div>
          {order.reject_reason && <p className="mt-3 text-sm text-errortext bg-errorbg rounded-xl p-3">Motivo: {order.reject_reason}</p>}
        </div>
        {order.mode === "delivery" && route && (
          <div className="bg-white rounded-3xl brand-shadow p-5 mt-4" data-testid="delivery-route">
            <div className="flex items-center gap-2 mb-3">
              <Footprints className="w-4 h-4 text-orange" />
              <span className="text-sm font-semibold text-navy">Percorso a piedi dal bar alla camera</span>
            </div>
            <MiniMap center={route.camera} markers={[{ lat: route.bar[0], lng: route.bar[1], name: route.vendor_name }]} route={route.route} height={200} />
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-navy/60">{route.vendor_name} → {route.bnb_name}</span>
              <span className="font-semibold text-navy" data-testid="route-eta">≈ {route.duration_min} min · {(route.distance_m / 1000).toFixed(1).replace(".", ",")} km</span>
            </div>
          </div>
        )}
        <Button onClick={load} variant="outline" className="w-full mt-4 rounded-full" data-testid="order-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna stato
        </Button>
        <p className="text-center text-xs text-navy/40 mt-4">Lo stato si aggiorna automaticamente. Conserva questo link.</p>
      </div>
    </div>
  );
}

function Row({ icon: Icon, k, v }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-cream flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-orange" /></div>
      <div className="flex-1 flex justify-between"><span className="text-navy/50">{k}</span><span className="font-medium text-navy text-right">{v}</span></div>
    </div>
  );
}
